/**
 * The claim index: the same claim, wherever it was made in the library.
 *
 * A refutation lands in one exploration. The same claim, reasoned again from a
 * different seed a week later, stands in another at full confidence, and
 * nothing joins the two — a library of forty explorations is forty graphs that
 * cannot see one another's records. This module is the join: every claim in
 * every stored exploration, keyed by its text, so that a refutation here can
 * reach its twin there, and a claim here can be told its twin was refuted.
 *
 * Two identities, from `core/similarity.ts`, because a claim is short. EXACT
 * identity is `textHash(foldText(claim))` — case and whitespace folded, nothing
 * else, the key the syndication gate already uses for a page. NEAR identity is
 * a Jaccard index over `SHINGLE_WORDS`-word shingles of the claim's words:
 * `simHash` is the right instrument for a page and the wrong one for a
 * sentence, since below `SIMHASH_MIN_CHARS` a handful of shingles leaves most
 * of its accumulators near zero and single-word edits flip bits wholesale. A
 * set of word triples compared directly has no such floor — a reader can count
 * the shared triples by hand — and three-word shingles are what make it about
 * PHRASING rather than topic, for the reason `similarity.ts` gives: two claims
 * on one subject share most of their words and almost none of their triples.
 *
 * `refuted` on an entry is `liveChannelOf(node) === 'refuted'` — a live
 * refuting source check or a refuting recorded outcome — and never a field the
 * model wrote about itself. The channel beside it is `channelOf`, the one the
 * canvas colours by.
 *
 * BOUNDED, AND NOT MEMOISED HERE. `libraryClaimIndex` reads every stored
 * exploration through `loadSession` — the mirror is synchronous, so this is a
 * parse per document and a walk per node, once — and returns a value the
 * CALLER holds for as long as the library has not changed. Building it inside a
 * render would parse the library per frame; a cache in this module would be a
 * second copy of the library that goes stale against the shelf, which is the
 * staleness class `loadDigest` refuses for the same reason. It never reads the
 * store: the open exploration is handed in by id and excluded, and nothing here
 * knows what is on screen.
 *
 * A leaf. It imports `./types`, `./similarity` and `./persist` and nothing
 * else at runtime.
 */

import { channelOf, liveChannelOf } from './types'
import type { Channel, Session } from './types'
import { SHINGLE_WORDS, foldText, textHash } from './similarity'
import { listRecent, loadSession } from './persist'

/** One claim, copied at the moment the index was built — the `ForeignRef`
 *  shape, for the `ForeignRef` reason: the exploration it names can be
 *  forgotten, and a row that then renders "(unknown)" is worse than a copy. */
export interface ClaimEntry {
  sessionId: string
  /** The exploration's seed. What names the source once the document is gone. */
  seed: string
  nodeId: string
  label: string
  claim: string
  channel: Channel
  /** `liveChannelOf(node) === 'refuted'` when the index was built. */
  refuted: boolean
}

/**
 * The index. `entries` is the public face — every claim read, in the order it
 * was read: explorations in the order given, nodes in `order` within each. The
 * two tables beside it are the lookups `twinsOf` runs and belong to this module.
 */
export interface ClaimIndex {
  readonly entries: readonly ClaimEntry[]
  /** How many explorations were read. */
  readonly sessions: number
  /** @internal `textHash(foldText(claim))` → indexes into `entries`. */
  readonly exact: ReadonlyMap<string, readonly number[]>
  /** @internal Word shingles per entry; null where the claim is shorter than one shingle. */
  readonly shingles: ReadonlyArray<ReadonlySet<string> | null>
}

/** What the index reads off an exploration. `frame` decides whether the seed
 *  itself is a claim; absent means it is (see `Session.frame`). */
export type ClaimSource = Pick<Session, 'id' | 'seed' | 'nodes' | 'order' | 'frame'>

/**
 * How much of two claims' word triples must be shared for them to be twins:
 * at least half, counted over the union — more triples shared than not.
 *
 * The arithmetic, so the line can be checked rather than trusted. A sixteen-word
 * claim has fourteen triples; one word changed inside it touches three, leaving
 * eleven of seventeen shared (0.65), so a one-word edit of a claim that length
 * is a twin. Two words changed apart from each other leave eight of twenty
 * (0.40), which is not. Under twelve words a single edit already falls short —
 * a ten-word claim keeps five of eleven — and that is the accepted cost: in a
 * claim that short, one word IS most of the claim.
 *
 * It errs toward NOT matching, the opposite way from `SYNDICATION_HAMMING_MAX`,
 * because the two errors trade places here. Over-merging there costs a genuine
 * refutation its loud channel, which the reader sees; over-merging HERE hands a
 * refutation to a claim that is not the same claim, and a label saying "your
 * twin was refuted elsewhere" about a different proposition is the failure
 * this app exists to prevent. A missed twin costs a reader one comparison they
 * were going to make anyway, with both claims printed side by side.
 */
export const CLAIM_TWIN_JACCARD_MIN = 0.5

/**
 * The words that flip a proposition, which shingles cannot see.
 *
 * Negation is the one difference that overlap measures badly by construction:
 * inserting "does not" into a long claim ADDS a few triples and removes none, so
 * a seventeen-word claim and its exact opposite keep eleven of twenty-one and
 * score about 0.52 — over the bar, on two propositions that cannot both be
 * true. That is precisely the over-merge the comment above says this app exists
 * to prevent, arriving through the one axis a threshold cannot be tuned against:
 * raising it far enough to separate these would reject genuine paraphrases, and
 * lowering it makes this worse.
 *
 * So polarity is checked apart from similarity. Two claims whose negation words
 * do not match are not near twins however alike they read, and an EXACT match is
 * untouched — identical text has identical polarity.
 *
 * Conservative on purpose. It counts markers rather than parsing them, so
 * "does not increase" and "fails to increase" read as different polarity and
 * miss each other. That is the cheap error: a missed twin costs a reader one
 * comparison they were going to make anyway, with both claims on screen.
 */
const NEGATIONS = new Set([
  'not',
  'no',
  'never',
  'cannot',
  'neither',
  'nor',
  'none',
  'without',
  'nothing',
  'fails',
  'fail',
])

/** How many polarity-flipping words a claim carries. Contractions count through
 *  their `n't`, which survives the fold as its own token only sometimes — so the
 *  suffix is tested rather than the word. */
export function negationCount(claim: string): number {
  return negationScopes(claim).length
}

/**
 * Where one clause ends and the next begins.
 *
 * A small closed set, not a parser: these are the words that join two
 * propositions in the sentences people actually write down as claims. Anything
 * finer would be a grammar, and anything coarser puts two propositions in one
 * scope, which is the bug this exists to prevent.
 */
/**
 * The other kind of clause break: the ones written as punctuation.
 *
 * A full stop ends a proposition at least as firmly as "while" does, and the
 * tokenizer below has to be told to keep them — `/[\p{L}\p{N}']+/` dropped
 * every one, so a claim written as two sentences was read as one clause.
 *
 * A COMMA is deliberately absent. It separates clauses in some sentences and
 * items in a list inside one clause, and this file counts rather than parses:
 * splitting on it would fragment a scope on a claim that reads as one
 * proposition, which is the direction that misses twins for no gain.
 */
const SENTENCE_BREAKS = new Set(['.', ';', ':', '!', '?'])

const CLAUSE_BREAKS = new Set([
  'while',
  'whilst',
  'whereas',
  'but',
  'although',
  'though',
  'however',
  'and',
  'or',
  'yet',
  'because',
  'since',
  'unless',
  'when',
  'if',
])

/**
 * WHICH PROPOSITION each negation applies to, not merely how many there are and
 * not merely what sits either side.
 *
 * Counting was the first guard and a window of two words either side was the
 * second. Both were defeated by the same move — a marker that CHANGES CLAUSE.
 * "A does not improve X while B improves Y" and "A improves X while B does not
 * improve Y" carry one marker each and share most of their words; with a
 * repeated clause prefix ("A in this trial does not improve …" against "B in
 * this trial does not improve …") they share the two-word window as well, and
 * a fixed window can always be out-run by a longer repeated prefix. These are
 * opposite allocations of one finding, and calling them twins hands a
 * refutation of one to the other.
 *
 * So a marker's scope is its CLAUSE. The claim is cut at `CLAUSE_BREAKS`, and
 * every clause carrying a marker contributes its own words IN ORDER, markers
 * removed — removing the marker is what lets "doesn't" and "does not" be
 * compared on what they negate rather than on how they were spelled. Two claims
 * match only if those scopes match as a multiset.
 *
 * In order, because a clause's words were sorted once and a sorted bag cannot
 * see a SYMMETRIC swap. The cases above differ in which words the marked clause
 * holds; swap the subject and the object instead — "A does not improve B"
 * against "B does not improve A" — and the bag is identical while the
 * allocation is reversed, so the two came back twins at about 0.7 overlap. The
 * sort bought nothing but order-insensitivity inside a clause, which is
 * permissiveness in the expensive direction.
 *
 * Still counting rather than parsing, in the spirit of `NEGATIONS` above, and
 * the error stays in the cheap direction the header names: a paraphrase INSIDE a
 * negated clause now reads as a different polarity and misses its twin. That is
 * a smaller loss than it looks, because a candidate has already had to clear
 * `CLAIM_TWIN_JACCARD_MIN` over the whole claim to be asked — these are near
 * copies, not loose restatements. It costs a reader one comparison they were
 * going to make anyway, with both claims on screen. The opposite mistake costs
 * a claim the refutation of its own contradiction.
 */
export function negationScopes(claim: string): string[] {
  // Sentence terminators come through as tokens of their own, because they are
  // clause breaks that happen to be punctuation rather than words. Dropped, two
  // sentences with no connective between them were ONE clause — and since the
  // marker is removed from whichever sentence holds it, "A does not improve X.
  // B does improve Y" and the version with `not` in the second sentence left the
  // identical word sequence behind. Word order cannot save a scope that spans
  // both allocations; only the boundary can.
  // Typographic apostrophes folded to the plain one FIRST, because everything
  // below is written in terms of it. A word processor turns `doesn't` into
  // `doesn’t` without asking, and the tokenizer then split it into `doesn` and
  // `t` — no marker, an empty scope, and a claim and its own negation matching
  // on overlap alone. `claimWords` needs no such fold: it splits on anything
  // that is not a letter or a digit, so both spellings already break the same
  // way there.
  const words =
    foldText(claim)
      .replace(/[\u2019\u02BC]/gu, "'")
      .match(/[\p{L}\p{N}']+|[.;:!?]/gu) ?? []
  const negated = (w: string): boolean => NEGATIONS.has(w) || w.endsWith("n't")
  const out: string[] = []
  let clause: string[] = []
  let marked = 0
  const close = () => {
    // One entry per marker, so "not … nor" inside one clause still counts twice
    // and cannot be matched by a clause carrying one.
    for (let i = 0; i < marked; i++) out.push(clause.join(' '))
    clause = []
    marked = 0
  }
  for (const word of words) {
    if (CLAUSE_BREAKS.has(word) || SENTENCE_BREAKS.has(word)) {
      close()
      continue
    }
    if (negated(word)) marked++
    else clause.push(word)
  }
  close()
  return out.sort()
}

/** The words of a claim: folded, split on anything that is not a letter or a
 *  digit, so punctuation and a trailing full stop cannot make two copies of one
 *  sentence read as two sentences. */
export function claimWords(claim: string): string[] {
  return foldText(claim)
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
}

/**
 * Cardinals that carry a quantity when spelled out.
 *
 * Closed on purpose, and deliberately including the ambiguous ones — `one` and
 * `half` are ordinary prose as often as they are measurements. Counting a word
 * that was not a quantity can only REJECT a pair of twins, which costs a row
 * nobody sees; missing one that was admits a refutation to a claim it does not
 * bear on, which is the thing this gate exists to stop. The asymmetry decides it.
 */
const NUMBER_WORDS = new Set([
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
  'twenty',
  'thirty',
  'forty',
  'fifty',
  'sixty',
  'seventy',
  'eighty',
  'ninety',
  'hundred',
  'thousand',
  'million',
  'billion',
  'half',
  'third',
  'quarter',
  'double',
  'triple',
  'twofold',
  'threefold',
  'tenfold',
])

const isQuantity = (w: string): boolean => /^\d+$/.test(w) || NUMBER_WORDS.has(w)

/**
 * Signs and comparisons, which `claimWords` drops with the rest of the
 * punctuation — and which are the difference between two propositions.
 *
 * `-10 percent` and `10 percent` fold to the same words, so they share every
 * shingle AND every quantity below: the gate this file just gained let them
 * through at a similarity of exactly 1. A sign is not punctuation, and neither
 * is `<` where somebody wrote a bound — nor `=` against `≠`, which say opposite
 * things about the same pair of numbers and folded to the same words too.
 */
const OPERATORS = new Set(['-', '+', '*', '/', '<', '>', '±', '<=', '>=', '=', '!='])

/**
 * Equivalent spellings of one operator, folded to a single form.
 *
 * Enumerating glyphs is how this rule kept springing leaks — a set holding `-`
 * and not `−` says a pasted minus is punctuation, and the next glyph after that
 * one would have said it again. Pasted scientific text carries U+2212 for minus
 * and an en dash for a range as a matter of course, and `≤` and `<=` are the
 * same proposition typed two ways.
 *
 * So the family is closed here rather than in the set: every dash becomes a
 * hyphen, every full-width form its ASCII twin, and `≤ ≥ ≠` the two-character
 * spellings that mean what they mean. `≠` and `!=` are then ONE quantity, which
 * they always were — the earlier pass had them as two, over-strict for want of
 * this step.
 *
 * `±` keeps its own glyph: there is no ASCII spelling of it to fold to.
 */
const OPERATOR_GLYPHS: Readonly<Record<string, string>> = {
  '\u2212': '-', // minus
  '\u2010': '-', // hyphen
  '\u2011': '-', // non-breaking hyphen
  '\u2012': '-', // figure dash
  '\u2013': '-', // en dash
  '\u2014': '-', // em dash
  '\u2015': '-', // horizontal bar
  '\ufe63': '-', // small hyphen-minus
  '\uff0d': '-', // full-width hyphen-minus
  '\uff0b': '+',
  '\uff1c': '<',
  '\uff1e': '>',
  '\uff1d': '=',
  '\u2264': '<=',
  '\u2a7d': '<=',
  '\u2265': '>=',
  '\u2a7e': '>=',
  '\u2260': '!=',
  '\u00d7': '*', // multiplication sign
  '\u22c5': '*', // dot operator
  '\u2217': '*', // asterisk operator
  '\uff0a': '*',
  '\u00f7': '/', // division sign
  '\u2215': '/', // division slash
  '\uff0f': '/',
}

const GLYPH = new RegExp(`[${Object.keys(OPERATOR_GLYPHS).join('')}]`, 'gu')

/** Words and numbers as `claimWords` reads them, plus the operators it drops. */
const QUANTITY_TOKEN = /<=|>=|!=|[-+*/<>±=]|[\p{L}\p{N}]+/gu

/**
 * A hyphen written TIGHT between two digits, which is how a range is written
 * and how arithmetic is not.
 *
 * Spacing is the only thing that tells `10-20 percent` from `20 - 10 units`,
 * and it is gone by the time there are tokens. So the range is resolved here,
 * on the text, before anything is tokenised — and every operator that survives
 * into the token stream is then a real operator, with no need to guess later
 * which ones separate rather than modify.
 *
 * The first rule tried to guess, by suppressing any operator between two
 * quantities. It could not tell `20 + 10 units` from `20 - 10 units`, nor
 * `10 < 20` from `10 > 20`: both signatures came out identical, at a
 * similarity of 1, which is the whole failure one layer further down.
 */
const RANGE_HYPHEN = /(\d)-(\d)/g

const quantityTokens = (claim: string): string[] =>
  foldText(claim)
    // Glyphs first, so the range rule below sees every dash as a dash.
    .replace(GLYPH, (g) => OPERATOR_GLYPHS[g]!)
    .replace(RANGE_HYPHEN, '$1 $2')
    .match(QUANTITY_TOKEN) ?? []

/**
 * The quantities a claim states, in order, each carrying the word that follows
 * it.
 *
 * The Jaccard bar and the negation scopes were the whole near-twin gate, and a
 * quantity is neither. Two otherwise identical sixteen-word claims promising a
 * 10 percent and a 50 percent reduction score 0.65 against a bar of 0.5 and
 * negate in the same places, so they passed as one claim — and `refutedTwinsOf`
 * then told a reader that a refutation of the first bears on the second. It is
 * the same failure the negation check above was written for, on the other axis:
 * a difference the overlap cannot see is the difference that matters.
 *
 * The following word comes along because it is usually the unit, and `10 mg` and
 * `10 g` are not the same quantity. `claimWords` has already dropped `%`, so a
 * bare `10%` reads as `10` where `10 percent` reads as `10 percent` — an
 * over-strict answer, and over-strict is the safe direction here.
 *
 * ORDERED rather than a multiset, like the negation scopes beside it: "A by 10
 * and B by 50" and "A by 50 and B by 10" are different claims.
 */
export function numericTerms(claim: string): string[] {
  const t = quantityTokens(claim)
  const out: string[] = []
  t.forEach((w, i) => {
    if (!isQuantity(w)) return
    // Every operator that reached here binds, the range having been resolved on
    // the text above. `20 + 10` and `20 - 10` are different quantities, and so
    // are `10 < 20` and `10 > 20`.
    const before = t[i - 1]
    const bound = before !== undefined && OPERATORS.has(before)
    // The unit, stepping over a hyphen so `10-fold` and `10 fold` are one
    // quantity rather than two answers to the same question.
    const after = t[i + 1] !== undefined && OPERATORS.has(t[i + 1]!) ? t[i + 2] : t[i + 1]
    const unit =
      after !== undefined && !isQuantity(after) && !OPERATORS.has(after) ? ` ${after}` : ''
    out.push(`${bound ? before : ''}${w}${unit}`)
  })
  return out
}

/** The claim's `SHINGLE_WORDS`-word shingles, or null where it has none — a
 *  claim shorter than one shingle has no near identity, only an exact one. */
export function claimShingles(claim: string): Set<string> | null {
  const words = claimWords(claim)
  if (words.length < SHINGLE_WORDS) return null
  const out = new Set<string>()
  for (let i = 0; i + SHINGLE_WORDS <= words.length; i++) {
    out.add(words.slice(i, i + SHINGLE_WORDS).join(' '))
  }
  return out
}

function jaccard(a: ReadonlySet<string>, b: ReadonlySet<string>): number {
  let shared = 0
  for (const s of a) if (b.has(s)) shared++
  const union = a.size + b.size - shared
  return union === 0 ? 0 : shared / union
}

/** The Jaccard index of two claims' shingle sets, 0–1. Zero where either has no
 *  shingle: an absent signature matches nothing, as in `similarity.ts`. */
export function claimSimilarity(a: string, b: string): number {
  const sa = claimShingles(a)
  const sb = claimShingles(b)
  if (!sa || !sb) return 0
  return jaccard(sa, sb)
}

/**
 * Build the index over the explorations given, in the order given.
 *
 * The seed node — the root, whose claim is the seed — is indexed only where the
 * frame reads it as a claim: under a `question` or a `goal` the root is not a
 * proposition, and a twin of "Why do birds migrate?" is not a claim anything
 * could refute. Its children are indexed whatever the frame; a candidate answer
 * or a route is a claim. A claim that folds to nothing is skipped, because every
 * such claim would otherwise be an exact twin of every other.
 */
export function buildClaimIndex(sessions: Iterable<ClaimSource>): ClaimIndex {
  const entries: ClaimEntry[] = []
  const exact = new Map<string, number[]>()
  const shingles: Array<ReadonlySet<string> | null> = []
  let count = 0
  for (const s of sessions) {
    count++
    const seedIsClaim = s.frame !== 'question' && s.frame !== 'goal'
    for (const id of s.order) {
      const n = s.nodes[id]
      if (!n) continue
      if (n.parentId === null && !seedIsClaim) continue
      const folded = foldText(n.claim)
      if (!folded) continue
      const at = entries.length
      entries.push({
        sessionId: s.id,
        seed: s.seed,
        nodeId: n.id,
        label: n.label,
        claim: n.claim,
        channel: channelOf(n),
        refuted: liveChannelOf(n) === 'refuted',
      })
      const key = textHash(folded)
      const bucket = exact.get(key)
      if (bucket) bucket.push(at)
      else exact.set(key, [at])
      shingles.push(claimShingles(n.claim))
    }
  }
  return { entries, sessions: count, exact, shingles }
}

/**
 * The index over every stored exploration, newest save first, read through
 * `loadSession` — never through localStorage, and never through the store.
 * `exceptId` leaves out the open exploration, whose claims are on screen and
 * are not "elsewhere". A row whose document no longer reads is skipped.
 */
export function libraryClaimIndex(exceptId?: string): ClaimIndex {
  const sessions: ClaimSource[] = []
  for (const entry of listRecent()) {
    if (entry.id === exceptId) continue
    const doc = loadSession(entry.id)
    if (doc) sessions.push(doc.session)
  }
  return buildClaimIndex(sessions)
}

export interface TwinOptions {
  /** One entry to leave out — the claim's own record, when it is in the index. */
  exclude?: { sessionId: string; nodeId: string }
}

/**
 * The twins of a claim: exact copies first, in index order, then near copies
 * at or above `CLAIM_TWIN_JACCARD_MIN`, the closest first and index order
 * within a tie. Deterministic for one index and one claim, so two readers of
 * the same library see the same list.
 */
export function twinsOf(ix: ClaimIndex, claim: string, opts?: TwinOptions): ClaimEntry[] {
  const folded = foldText(claim)
  if (!folded) return []
  const keep = (e: ClaimEntry): boolean =>
    !opts?.exclude || e.sessionId !== opts.exclude.sessionId || e.nodeId !== opts.exclude.nodeId
  const exactAt = ix.exact.get(textHash(folded)) ?? []
  const out = exactAt.map((i) => ix.entries[i]!).filter(keep)

  const mine = claimShingles(claim)
  if (!mine) return out
  const taken = new Set(exactAt)
  const polarity = negationScopes(claim).join('\n')
  const quantities = numericTerms(claim).join('\n')
  const near: Array<{ at: number; score: number }> = []
  ix.shingles.forEach((theirs, at) => {
    if (!theirs || taken.has(at)) return
    const score = jaccard(mine, theirs)
    if (score < CLAIM_TWIN_JACCARD_MIN) return
    // Similar AND negated in the same PLACES. See `negationScopes`: a claim and
    // its negation overlap enough to clear the bar, and so do two claims that
    // move one negation from the first half to the second — calling either pair
    // the same claim hands a refutation to the proposition it refutes.
    if (negationScopes(ix.entries[at]!.claim).join('\n') !== polarity) return
    // AND stating the same quantities. See `numericTerms`: the overlap cannot
    // see the difference between a tenth and a half, and neither could this.
    if (numericTerms(ix.entries[at]!.claim).join('\n') !== quantities) return
    near.push({ at, score })
  })
  near.sort((a, b) => b.score - a.score || a.at - b.at)
  for (const { at } of near) {
    const e = ix.entries[at]!
    if (keep(e)) out.push(e)
  }
  return out
}

/** The twins whose own record is refuted — the reach of a refutation, seen
 *  from the claim it reaches. */
export function refutedTwinsOf(ix: ClaimIndex, claim: string): ClaimEntry[] {
  return twinsOf(ix, claim).filter((e) => e.refuted)
}

/**
 * For every refuted node in `s`, its twins ELSEWHERE that are not themselves
 * refuted — the claims a refutation here has something to say to. Rows are in
 * `s.order`; a refuted node with nothing to reach has no row, because a row
 * that names nobody would be a count of refutations, which the graph already
 * has. Entries from `s` itself are never "elsewhere", whether or not the index
 * was built with `s` left out.
 */
export function reachOfRefutation(
  ix: ClaimIndex,
  s: Pick<Session, 'id' | 'nodes' | 'order'>,
): Array<{ nodeId: string; twins: ClaimEntry[] }> {
  const out: Array<{ nodeId: string; twins: ClaimEntry[] }> = []
  for (const id of s.order) {
    const n = s.nodes[id]
    if (!n || liveChannelOf(n) !== 'refuted') continue
    const twins = twinsOf(ix, n.claim).filter((e) => e.sessionId !== s.id && !e.refuted)
    if (twins.length) out.push({ nodeId: id, twins })
  }
  return out
}
